const DAY_MS = 24 * 60 * 60 * 1000;

export const SYNTHETIC_SCENARIOS = Object.freeze([
  {
    key: "assessment_dropoff",
    label: "Assessment drop-off",
    description: "Completion falls around the capacity section, concentrated in the evening cohort.",
    primaryMetric: "assessment_completion_rate",
  },
  {
    key: "program_decline",
    label: "Program-level decline",
    description: "Positive response association falls inside one synthetic program.",
    primaryMetric: "positive_response_rate",
  },
  {
    key: "intervention_difference",
    label: "Intervention difference",
    description: "One synthetic intervention shows a stronger response association for one baseline pattern.",
    primaryMetric: "positive_response_rate",
  },
  {
    key: "engagement_decline",
    label: "Engagement decline",
    description: "Follow-up participation falls after a simulated scheduling change.",
    primaryMetric: "follow_up_completion_rate",
  },
]);

const PROGRAMS = ["Foundations", "Guided Implementation", "Recovery Skills", "Performance"];
const COHORTS = ["Morning", "Afternoon", "Evening"];
const INTERVENTIONS = [
  "Movement primer",
  "Skill practice",
  "Recovery transition",
  "Outdoor regulation walk",
];
const BASELINE_PATTERNS = [
  "Higher mobilization",
  "Lower usable energy",
  "Mixed activation",
  "Stable capacity",
];
const ASSESSMENT_SECTIONS = [
  "Profile",
  "Nervous-system pattern",
  "Behavioral responses",
  "Capacity and readiness",
  "Schedule and environment",
  "Review",
];

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

const seededRandom = (seedValue) => {
  let seed = Number(seedValue) >>> 0;
  return () => {
    seed += 0x6d2b79f5;
    let value = seed;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
};

const pick = (random, values) => values[Math.floor(random() * values.length)];

const weightedPick = (random, values) => {
  const total = values.reduce((sum, item) => sum + item.weight, 0);
  let cursor = random() * total;
  for (const item of values) {
    cursor -= item.weight;
    if (cursor <= 0) return item.value;
  }
  return values[values.length - 1].value;
};

const isoAfter = (value, minutes) =>
  value ? new Date(new Date(value).getTime() + minutes * 60 * 1000).toISOString() : null;

const randomDate = (random, start, end) =>
  new Date(start.getTime() + random() * Math.max(1, end.getTime() - start.getTime())).toISOString();

const scenarioByKey = (key) =>
  SYNTHETIC_SCENARIOS.find((scenario) => scenario.key === key) || SYNTHETIC_SCENARIOS[0];

export const normalizeSyntheticConfig = (input = {}) => {
  const anchor = new Date(input.anchorDate || new Date().toISOString());
  const validAnchor = Number.isNaN(anchor.getTime()) ? new Date("2026-08-26T12:00:00.000Z") : anchor;
  return {
    scenario: scenarioByKey(input.scenario).key,
    participants: clamp(Math.round(Number(input.participants) || 360), 80, 1200),
    days: clamp(Math.round(Number(input.days) || 90), 30, 180),
    programCount: clamp(Math.round(Number(input.programCount) || 3), 1, PROGRAMS.length),
    seed: clamp(Math.round(Number(input.seed) || 2048), 1, 999999),
    anchorDate: validAnchor.toISOString(),
  };
};

const assessmentProbability = ({ scenario, period, cohort }) => {
  let probability = cohort === "Evening" ? 0.79 : 0.84;
  if (scenario === "assessment_dropoff" && period === "current") {
    probability = cohort === "Evening" ? 0.52 : 0.78;
  }
  return probability;
};

const interventionCompletionProbability = ({ scenario, period, cohort }) => {
  let probability = cohort === "Evening" ? 0.66 : 0.72;
  if (scenario === "engagement_decline" && period === "current" && cohort === "Evening") {
    probability -= 0.1;
  }
  return probability;
};

const followUpProbability = ({ scenario, period, cohort }) => {
  let probability = cohort === "Evening" ? 0.62 : 0.69;
  if (scenario === "engagement_decline" && period === "current") {
    probability = cohort === "Evening" ? 0.35 : 0.48;
  }
  return probability;
};

const positiveResponseProbability = ({
  scenario,
  period,
  program,
  intervention,
  baselinePattern,
}) => {
  let probability = 0.61;
  if (scenario === "program_decline" && period === "current") {
    probability = program === "Guided Implementation" ? 0.31 : 0.56;
  }
  if (scenario === "intervention_difference" && period === "current") {
    probability =
      intervention === "Skill practice" && baselinePattern === "Higher mobilization"
        ? 0.95
        : 0.61;
  }
  return probability;
};

const abandonmentSection = ({ random, scenario, period, cohort }) => {
  if (scenario === "assessment_dropoff" && period === "current" && cohort === "Evening") {
    return weightedPick(random, [
      { value: "Capacity and readiness", weight: 72 },
      { value: "Behavioral responses", weight: 10 },
      { value: "Schedule and environment", weight: 10 },
      { value: "Review", weight: 8 },
    ]);
  }
  return weightedPick(
    random,
    ASSESSMENT_SECTIONS.slice(1).map((value, index) => ({
      value,
      weight: index === 2 ? 24 : 19,
    })),
  );
};

export const generateSyntheticDataset = (input = {}) => {
  const config = normalizeSyntheticConfig(input);
  const random = seededRandom(config.seed);
  const scenario = config.scenario;
  const anchor = new Date(config.anchorDate);
  const rangeStart = new Date(anchor.getTime() - config.days * DAY_MS);
  const split = new Date(rangeStart.getTime() + (config.days / 2) * DAY_MS);
  const programs = PROGRAMS.slice(0, config.programCount);
  const records = [];

  for (let index = 0; index < config.participants; index += 1) {
    const period = index < Math.floor(config.participants / 2) ? "comparison" : "current";
    const dateStart = period === "comparison" ? rangeStart : split;
    const dateEnd = period === "comparison" ? split : anchor;
    const startedAt = randomDate(random, dateStart, dateEnd);
    const program = pick(random, programs);
    const cohort = pick(random, COHORTS);
    const intervention =
      scenario === "intervention_difference" && period === "current"
        ? weightedPick(random, INTERVENTIONS.map((value) => ({
            value,
            weight: value === "Skill practice" ? 80 : 6.7,
          })))
        : pick(random, INTERVENTIONS);
    const baselinePattern =
      scenario === "intervention_difference" && period === "current"
        ? weightedPick(random, BASELINE_PATTERNS.map((value) => ({
            value,
            weight: value === "Higher mobilization" ? 80 : 6.7,
          })))
        : pick(random, BASELINE_PATTERNS);
    const completed = random() < assessmentProbability({ scenario, period, cohort });
    const durationMinutes = completed ? Math.round(12 + random() * 20) : null;
    const assigned = completed && random() < 0.9;
    const interventionCompleted =
      assigned && random() < interventionCompletionProbability({ scenario, period, cohort });
    const didNotComplete = assigned && !interventionCompleted;
    const followUpRequested = assigned && random() < 0.94;
    const followUpCompleted =
      followUpRequested && random() < followUpProbability({ scenario, period, cohort });
    const positive =
      followUpCompleted &&
      random() <
        positiveResponseProbability({
          scenario,
          period,
          program,
          intervention,
          baselinePattern,
        });
    const preResponse = followUpCompleted ? Math.round(2 + random() * 2) : null;
    const responseChange = followUpCompleted
      ? positive
        ? weightedPick(random, [
            { value: 1, weight: 45 },
            { value: 2, weight: 38 },
            { value: 3, weight: 17 },
          ])
        : weightedPick(random, [
            { value: -1, weight: 22 },
            { value: 0, weight: 78 },
          ])
      : null;
    const postResponse = followUpCompleted
      ? clamp(preResponse + responseChange, 1, 5)
      : null;

    records.push({
      participantId: `demo-${config.seed}-${String(index + 1).padStart(4, "0")}`,
      dataMode: "synthetic",
      period,
      program,
      cohort,
      referralSource: pick(random, ["Direct", "Professional referral", "Community", "Campaign"]),
      device: pick(random, ["Mobile", "Desktop", "Tablet"]),
      baselinePattern,
      assessment: {
        startedAt,
        completed,
        completedAt: completed ? isoAfter(startedAt, durationMinutes) : null,
        durationMinutes,
        abandonmentSection: completed
          ? null
          : abandonmentSection({ random, scenario, period, cohort }),
      },
      intervention: {
        name: intervention,
        assigned,
        assignedAt: assigned ? isoAfter(startedAt, durationMinutes + 1) : null,
        completed: interventionCompleted,
        completedAt: interventionCompleted ? isoAfter(startedAt, durationMinutes + 120) : null,
        didNotComplete,
      },
      followUp: {
        requested: followUpRequested,
        requestedAt: followUpRequested ? isoAfter(startedAt, durationMinutes + 1440) : null,
        completed: followUpCompleted,
        completedAt: followUpCompleted ? isoAfter(startedAt, durationMinutes + 1560) : null,
      },
      response: {
        pre: preResponse,
        post: postResponse,
        change: followUpCompleted ? postResponse - preResponse : null,
        positive: Boolean(followUpCompleted && postResponse > preResponse),
      },
    });
  }

  return {
    id: `synthetic-${config.scenario}-${config.seed}`,
    mode: "synthetic",
    scenario: scenarioByKey(config.scenario),
    config,
    generatedAt: new Date().toISOString(),
    records,
  };
};
